import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * useNotes — GET/POST/DELETE /api/notes
 *
 * @param {string} courseId
 * @param {string} lessonId
 * @returns {{ notes, loading, error, addNote, deleteNote, adding, noteInput, setNoteInput }}
 */
export function useNotes(courseId, lessonId) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [noteInput, setNoteInput] = useState('');

  const fetchNotes = useCallback(async () => {
    if (!courseId || !lessonId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/notes`, {
        params: { course_id: courseId, lesson_id: lessonId },
        headers: getAuthHeaders(),
      });
      // Backend: { notes: [...] } ya da düz array
      setNotes(res.data.notes || res.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [courseId, lessonId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const addNote = useCallback(async (content) => {
    if (!content || !content.trim()) return;
    setAdding(true);
    try {
      await axios.post(
        `${API}/notes`,
        { course_id: courseId, lesson_id: lessonId, content: content.trim() },
        { headers: getAuthHeaders() }
      );
      setNoteInput('');
      await fetchNotes();
    } finally {
      // Hata bileşene iletilir; bileşen toast ile gösterir
      setAdding(false);
    }
  }, [courseId, lessonId, fetchNotes]);

  const deleteNote = useCallback(async (noteId) => {
    try {
      await axios.delete(`${API}/notes/${noteId}`, { headers: getAuthHeaders() });
      // Optimistik: state'den hemen çıkar
      setNotes(prev => prev.filter(n => (n.note_id || n._id || n.id) !== noteId));
    } catch (err) {
      // Başarısız olursa yeniden fetch
      await fetchNotes();
      throw err;
    }
  }, [fetchNotes]);

  return {
    notes,
    loading,
    error,
    addNote,
    deleteNote,
    adding,
    noteInput,
    setNoteInput,
    refetch: fetchNotes,
  };
}
